import { FREE_PRODUCT_LIMIT, PLANS, RECOMMENDATION_BUFFER, isCapTier, planPrice } from './plans';

/**
 * Wert des GA4-Konversionsereignisses `catalog_synced`: der monatliche
 * USD-Listenpreis des Kapazitaets-Bands, das der Katalog eines Kontos braucht.
 * Google Ads lernt aus diesem Wert, Haendler mit grossen Katalogen zu suchen.
 *
 * Dieselbe Formel wie `bandForProducts()` auf der Marketing-Seite
 * (feedoptimizer-web `planTiers.ts`) und wie `getRecommendedPlan` ohne
 * `currentTier`: kleinstes Band mit `productLimit >= Produkte x 1,15`. So
 * meldet die Konversion genau das Band, das Preisseite und App dem Haendler
 * auch empfehlen, statt einer dritten, eigenen Zuordnung.
 *
 * Free traegt den Wert 0: ein Katalog bis `FREE_PRODUCT_LIMIT` Produkte hat
 * kein Umsatzpotenzial, und die Gebotsstrategie soll solche Anmeldungen nicht
 * gezielt suchen.
 *
 * `offer` (Katalog samt Puffer groesser als das groesste Band, also ab
 * 86.957 Produkten) traegt den Preis des groessten Bands (499 USD) und keinen
 * erfundenen Angebotspreis. Die echte Groesse steht als `catalog_products` im
 * Ereignis selbst.
 *
 * Der Wert fliesst in die Gebotsstrategie eines Fremdsystems (Google Ads) und
 * verschiebt dort echtes Budget. Ein stiller Drift der Staffel faellt hier
 * nicht auf, deshalb pinnt der Waechter-Test die Staffel als Literal.
 */

export const CATALOG_CONVERSION_CURRENCY = 'USD' as const;

export type CatalogBand = 'free' | 'offer' | `cap${number}`;

export interface CatalogConversionValue {
  value: number;
  band: CatalogBand;
  currency: typeof CATALOG_CONVERSION_CURRENCY;
}

export function catalogConversionValue(catalogProducts: number): CatalogConversionValue {
  const products = Number.isFinite(catalogProducts) && catalogProducts > 0 ? catalogProducts : 0;

  if (products <= FREE_PRODUCT_LIMIT) {
    return { value: 0, band: 'free', currency: CATALOG_CONVERSION_CURRENCY };
  }

  const capPlans = PLANS.filter((p) => isCapTier(p.tier));
  const target = products * RECOMMENDATION_BUFFER;
  const match = capPlans.find((p) => p.productLimit >= target);
  if (match) {
    return {
      value: planPrice(match, 'usd', 'monthly'),
      band: match.tier as CatalogBand,
      currency: CATALOG_CONVERSION_CURRENCY,
    };
  }

  const largest = capPlans[capPlans.length - 1];
  return {
    value: planPrice(largest, 'usd', 'monthly'),
    band: 'offer',
    currency: CATALOG_CONVERSION_CURRENCY,
  };
}
